import React, { useState, Suspense, useEffect, useRef, useMemo } from 'react';
import { Camera, Download, Maximize, Image as ImageIcon, RefreshCw, Minimize2, ChevronDown, Loader2, RotateCw, FlipHorizontal, FlipVertical, CloudUpload } from 'lucide-react';
import { styles, NodeResolutionTag, getDownscaleOptions, calculateDownscaleSize, getCompressTargets, downloadFile, buildDownloadFilename, ToolBtn, formatBytes } from './NodeCommons';
import QuickTooltip from '../components/QuickTooltip';
import { getFilename } from '../utils';
import SkillboxToolbar from './SkillboxToolbar';
import { ossThumb, isOwnOssUrl, getCachedDimensions, setCachedDimensions, fetchOssImageInfo } from '../utils/imageThumb';
import localCacheService from '../services/localCacheService';
import { API_BASE_URL } from '../constants';
import api from '../api';
import { MIN_NODE_WIDTH } from '../editorUtils';
import { nonEmptyToolsets, getTopPreset } from '../utils/presetTree';

// --- 懒加载优化：只在点击按钮时加载复杂弹窗代码 ---
const AnnotationModal = React.lazy(() => import('../AnnotationModal'));
const ImageCropperModal = React.lazy(() => import('../ImageCropperModal')); 
const FullscreenModal = React.lazy(() => import('../FullscreenModal'));

// P1: React.memo 防止父级 canvas 重渲染时级联到不相关的节点视图
export default React.memo(function ImageNodeView({ id, data, selected, isSingleSelect = false, xPos, yPos, handleUpdate, zoom = 1, showConsole, onToggleConsole }) {
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [activeModal, setActiveModal] = useState(null); 
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 初值优先取尺寸缓存：本节点（或同 URL 的其它节点）之前探到过原图尺寸，
  //   重挂后直接带标签，无需重探 → 标签不再「消失后要重进才回来」。
  //   优先级：节点存的 width/height（生成时写入，自建化不依赖 OSS）→ 尺寸缓存。
  const [imgRes, setImgRes] = useState(() =>
    (data.width && data.height) ? { w: data.width, h: data.height } : getCachedDimensions(data.output));
  // ⚡ 实际贴到可见 <img> 上的 URL。与 cardSrc 解耦，用于「先解码后替换」：
  //   cardSrc 变化时（缩略图→全图 blob、远程→缓存）先离屏解码新图，解码完成
  //   才推进 committedSrc，可见画面始终显示旧帧直到新帧就绪 → 消除「二刷/闪一下」。
  const [committedSrc, setCommittedSrc] = useState(null);
  // 是否在画布上显示全图：仅当本节点被「单选过」（凑近看/要编辑）才置 true 并保持。
  //   未选中一律显示 1024 缩略图 —— 即使本地已灌好全图（localCacheUrl，来自后台镜像预热）
  //   也不贴全图，避免画布同时解码几百张全分辨率位图吃满内存/显存。
  const [showFullRes, setShowFullRes] = useState(false);
  const [showDownscaleMenu, setShowDownscaleMenu] = useState(false);
  // 🆕 压缩体积：当前文件字节数 + 压缩菜单开关 + 压缩中状态
  const [fileBytes, setFileBytes] = useState(0);
  const [showCompressMenu, setShowCompressMenu] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0); // 0-100，压缩进度
  const compressWorkerRef = useRef(null);
  const [isRebuildingPreview, setIsRebuildingPreview] = useState(
    // 初始值：如果有 annotationData 但没有 annotationPreview，说明需要重建
    !!(data.annotationData && !data.annotationPreview)
  );
  const [isMerging, setIsMerging] = useState(false);  // 🆕 合并导出中状态
  const [isMultiLayerMode, setIsMultiLayerMode] = useState(false);  // 🆕 多图层模式状态（稳定版，避免闪烁）
  const [, setImgRetryCount] = useState(0); // 图片加载失败重试计数
  const imgRetryTimerRef = useRef(null); // 重试定时器（防泄漏）

  // 🆕 原始图片 URL（用于传给绘图板作为底图）
  // 注意:必须使用真实云存储 URL 而非 blob URL,否则序列化后 blob URL 失效导致图层丢失
  const originalImageUrl = data.output && !data.output.startsWith('local-cache://') ? data.output : null;
  // 🆕 显示 URL：优先用绘图板预览（图层合成），否则用本地缓存或原图
  const displayUrl = data.annotationPreview || data.localCacheUrl || originalImageUrl;
  // 是否正在从本地缓存解析 blob URL（local-cache:// 还未解析完毕时的过渡态）
  const isResolvingCache = !!(data.output?.startsWith('local-cache://') && !data.localCacheUrl);

  // ⚡ 卡片显示专用 URL（cardSrc）——与 displayUrl 解耦
  //   displayUrl 必须是全尺寸（喂绘图板底图 / 下载 / 分辨率&体积测量），
  //   但画布卡片只需要看清缩略即可，拉全尺寸原图是首开慢的主因。
  //   优先级：
  //     1. annotationPreview（图层合成结果）→ 原样，不缩
  //     2. localCacheUrl（已下载好的全图 blob）→ 原样，零网络、画质最佳
  //     3. 远程原图 → 优先 thumbUrl（服务端 400px webp），否则 ossThumb(768)，
  //        非自家 OSS 图（如 MJ 桶）ossThumb 会原样返回，保证不断图
  const cardSrc = useMemo(() => {
    if (data.annotationPreview) return data.annotationPreview;  // PS 编辑预览，始终显示
    if (originalImageUrl && isOwnOssUrl(originalImageUrl)) {
      // 选中过且全图已就绪 → 画布贴全图（本地缓存 blob）。
      if (showFullRes && data.localCacheUrl) return data.localCacheUrl;
      // 默认卡片：优先用「存储好的缩略图文件」data.thumbUrl（768，OSS 直接给、浏览器好缓存
      //   → 重复打开秒开）；老图/上传图暂无 thumbUrl 时回退现算 ossThumb(768)（OSS 按需处理）。
      //   —— 不因 localCacheUrl 存在就贴全图，避免画布同时解码几百张全分辨率位图吃满内存/显存
      //   （Figma/tldraw 同理：手上有原图也只在画布渲染降采样版）。
      return data.thumbUrl || ossThumb(originalImageUrl, 768);
    }
    // 非自家 OSS（local-cache:// 已解析的 blob / 第三方图）：无法拼缩略参数，用现有 URL
    if (data.localCacheUrl) return data.localCacheUrl;
    if (originalImageUrl) return originalImageUrl;
    return displayUrl;
  }, [data.annotationPreview, data.thumbUrl, data.localCacheUrl, originalImageUrl, displayUrl, showFullRes]);

  // 项目加载后自动从 annotationData 还原预览图
  useEffect(() => {
    if (data.annotationData && !data.annotationPreview) {
      setIsRebuildingPreview(true);
      import('../annotationRenderer').then(({ renderAnnotationPreview }) => {
        renderAnnotationPreview(data.annotationData).then(previewUrl => {
          if (previewUrl) handleUpdate({ annotationPreview: previewUrl });
          setIsRebuildingPreview(false);
        }).catch(() => setIsRebuildingPreview(false));
      }).catch(() => setIsRebuildingPreview(false));
    } else {
      setIsRebuildingPreview(false);
    }
  }, [data.annotationData, data.annotationPreview, handleUpdate]);
  
  // 🔧 记录上一次的 output，用于检测是否换了新图
  const prevOutputRef = useRef(data.output);
  const prevExportRef = useRef(data.requestExport);

  // 🆕 响应来自 CustomNode 的导出请求
  useEffect(() => {
    if (data.requestExport && data.requestExport !== prevExportRef.current) {
        prevExportRef.current = data.requestExport;
        handleMergeAndExport();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.requestExport]);
  // 🆕 追踪 refFiles 和图层数据变化
  const prevRefFilesRef = useRef(data.refFiles);
  const annotationDataRef = useRef(data.annotationData);
  annotationDataRef.current = data.annotationData;
  const blobUrlFixedRef = useRef(false);  // 记录 blob URL 是否已修复，避免重复触发
  // ⚡ 分辨率标签真值探测：缩略图 onLoad 给的是临时值，节点滚进视口后探一次
  //   全尺寸原图拿到真实像素。fullResMeasuredRef 标记已探到真值，避免重复+防缩略覆盖。
  const fullResMeasuredRef = useRef(false);
  const resolveUrlTriedRef = useRef(false);  // A3: 死链解析只尝试一次，避免循环
  const fullImgFetchedRef = useRef(false);   // 懒加载：进视口拉全图只触发一次
  const ioRef = useRef(null);            // IntersectionObserver 实例
  const [inViewport, setInViewport] = useState(false);
  // 回调 ref：可见 <img> 挂载/卸载时自动 (dis)connect 视口观察器。
  //   IntersectionObserver 不可用时直接置 inViewport=true（退化为「始终探测」）。
  const observeCardEl = useRef((el) => {
    if (ioRef.current) { ioRef.current.disconnect(); ioRef.current = null; }
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setInViewport(true); return; }
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) if (en.isIntersecting) { setInViewport(true); io.disconnect(); return; }
    }, { rootMargin: '200px' });
    io.observe(el);
    ioRef.current = io;
  }).current;
  
  // 🛡️ 节点取消选中时，自动关闭降分辨率/压缩菜单
  useEffect(() => {
    if (!selected) {
      setShowDownscaleMenu(false);
      setShowCompressMenu(false);
    }
  }, [selected]);
  
  // --- Loading 逻辑优化：最少展示 2 秒 ---
  const [resourceLoaded, setResourceLoaded] = useState(false); // 图片真正加载完成
  const [minTimePassed, setMinTimePassed] = useState(false);   // 最小展示时间已到

  useEffect(() => {
    // 只有当 data.useLoadingEffect 为 true (来自项目库) 时，才强制等待
    if (data.useLoadingEffect) {
        const timer = setTimeout(() => {
            setMinTimePassed(true);
        }, 500);
        return () => clearTimeout(timer);
    } else {
        // 本地上传或无需动效，直接视为时间已到
        setMinTimePassed(true);
    }
  }, [data.useLoadingEffect]);

  // 连线删除时：已编辑过的图层保留，不自动清理
  // 已烘焙到编辑器中的图层应当持久保存，断线只影响外部叠加预览（unbakedOverlayRefs 自动计算）
  // 避免不必要的 annotationPreview 重建导致多次闪烁
  useEffect(() => {
    prevRefFilesRef.current = data.refFiles;
  }, [data.refFiles]);

  // ⚡ cardSrc 变化 → 先解码后替换（decode-then-swap）
  //   首帧（committedSrc 为空）：直接贴，靠 onLoad 触发 fade-in。
  //   后续切换（已有旧帧）：离屏 new Image().decode() 解码新图，成功后再切
  //     committedSrc，可见画面无空窗、不掉透明度 → 不再「二刷」。
  //   blob:/data: 已在内存，decode 近乎瞬时；解码失败则回退直接替换。
  useEffect(() => {
    if (!cardSrc) return;
    // 首帧或与当前一致：直接提交，走原有 onLoad → fade-in 流程
    if (!committedSrc) {
      setCommittedSrc(cardSrc);
      setResourceLoaded(false);
      setImgRetryCount(0);
      return;
    }
    if (cardSrc === committedSrc) return;

    let cancelled = false;
    const pre = new Image();
    pre.decoding = 'async';
    // 不设 crossOrigin：可见 <img> 也未设，保持同一 HTTP 缓存槽，避免二次下载
    pre.src = cardSrc;
    const commit = () => {
      if (cancelled) return;
      // 新图已解码好，无缝替换：保持 resourceLoaded=true，画面不掉透明度
      setCommittedSrc(cardSrc);
    };
    if (pre.decode) {
      pre.decode().then(commit).catch(() => {
        // 解码失败（跨域/格式）→ 回退直接替换，交给 <img> onError 兜底
        if (!cancelled) setCommittedSrc(cardSrc);
      });
    } else {
      pre.onload = commit;
      pre.onerror = () => { if (!cancelled) setCommittedSrc(cardSrc); };
    }
    return () => {
      cancelled = true;
      if (imgRetryTimerRef.current) { clearTimeout(imgRetryTimerRef.current); imgRetryTimerRef.current = null; }
    };
  }, [cardSrc, committedSrc]);

  // 底图变了（换新图/切首图）→ 分辨率真值失效，允许重新探测
  useEffect(() => {
    fullResMeasuredRef.current = false;
  }, [data.output]);

  // ⚡ 分辨率/体积探测（所有卡片挂载即探，不依赖视口/选中，跟原图下载彻底解耦）
  //   走 OSS image/info（几百字节 JSON），只读原图的宽/高/体积，不下载图片本身。
  //   这样即便卡片永远只显示缩略图，左上角分辨率&体积标签也能稳定常驻
  //   （修掉「刷新后标签时有时无」——旧逻辑把探测绑在视口里，离屏/慢加载就填不上）。
  //   卡片若已贴全图（blob/preview/非OSS原图），onLoad 读到的 naturalWidth 即真值，无需探。
  useEffect(() => {
    if (fullResMeasuredRef.current) return;
    if (!originalImageUrl) return;
    // 🥇 节点已存 width/height（生成时写入，自建化优先）→ 直接用，不探 OSS
    if (data.width && data.height) {
      fullResMeasuredRef.current = true;
      setImgRes(prev => (prev && prev.w === data.width && prev.h === data.height) ? prev : { w: data.width, h: data.height });
      setCachedDimensions(originalImageUrl, data.width, data.height);
      return;
    }
    // 已有尺寸缓存 → 直接用，不必探测
    const cached = getCachedDimensions(originalImageUrl);
    if (cached) {
      fullResMeasuredRef.current = true;
      setImgRes(prev => (prev && prev.w === cached.w && prev.h === cached.h) ? prev : cached);
      return;
    }
    // 卡片显示的不是缩略图（用了 blob/preview/原图）→ onLoad 已是真值
    const cardIsThumb = isOwnOssUrl(originalImageUrl) && cardSrc !== originalImageUrl
                        && !cardSrc?.startsWith('blob:') && cardSrc !== data.annotationPreview;
    if (!cardIsThumb) { fullResMeasuredRef.current = true; return; }

    let cancelled = false;
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    (async () => {
      // ① 首选 OSS image/info（轻量可靠，几百字节，不下载全图）
      const info = await fetchOssImageInfo(originalImageUrl, ac?.signal);
      if (cancelled) return;
      if (info) {
        fullResMeasuredRef.current = true;
        setImgRes({ w: info.w, h: info.h });
        if (info.bytes > 0) setFileBytes(info.bytes); // 顺带填体积，省一次原图 fetch
        return;
      }
      // ② 兜底：下载全图读 naturalWidth —— 仅在单选时才做，
      //    避免为「非自家 OSS 且离屏」的图在浏览阶段就下整张原图。
      if (!isSingleSelect) return;
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        fullResMeasuredRef.current = true;
        if (probe.naturalWidth) {
          setImgRes({ w: probe.naturalWidth, h: probe.naturalHeight });
          setCachedDimensions(originalImageUrl, probe.naturalWidth, probe.naturalHeight);
        }
      };
      probe.onerror = () => {};
      probe.src = originalImageUrl;
    })();
    return () => { cancelled = true; ac?.abort(); };
  }, [originalImageUrl, cardSrc, data.annotationPreview, isSingleSelect, data.width, data.height]);

  // 🔧 节点框比例锁定「原图真值」——修复：素材库缩略图常被裁成 1:1 / 竖图(角色 86:142)，
  //   而卡片默认贴的正是这张缩略图，onLoad 读到的 naturalWidth 是「裁剪后」比例，
  //   会把节点框拉成竖屏；一旦单选升级贴全图，onLoad 又按原图重算 → 表现为「一点选就变回来」。
  //   imgRes 已由上方探测拿到原图真实宽高（data.width/height / 尺寸缓存 / OSS image/info），
  //   这里据此把节点框比例校回原图，与缩略图实际比例无关。
  //   —— annotationPreview（图层合成）尺寸另有逻辑，跳过；_preserveSize（降级就地替换）期间跳过。
  useEffect(() => {
    if (!imgRes || !imgRes.w || !imgRes.h) return;
    if (data.annotationPreview) return;      // PS 预览尺寸不由原图决定
    if (data._preserveSize) {
      return;
    }
    if (data.params?.ratio) return;          // 用户选择了比例：保持选择的尺寸，不随实际图片比例调整
    const cs = data.customSize;
    if (!cs || !(cs.width > 0) || !(cs.height > 0)) return;  // 无尺寸时交给 onLoad 首次计算
    const nodeRatio = cs.width / cs.height;
    const mediaRatio = imgRes.w / imgRes.h;
    // 容差 5%：与 onLoad 的比例不匹配检测一致，避免浮点误差反复触发
    if (Math.abs(nodeRatio - mediaRatio) / mediaRatio <= 0.05) return;
    // 🔧 修复：检测初始正方形尺寸（240×240），跳过重新计算，等待 img.onload 设置正确尺寸
    if (cs.width === 240 && cs.height === 240) {
      console.log(`[ImageNodeView] 跳过重算 - 等待 img.onload`, {
        节点ID: id,
        当前尺寸: `${cs.width}×${cs.height}`,
        服务器返回尺寸: `${imgRes.w}×${imgRes.h}`
      });
      return;
    }
    // 以当前节点长边为基准，只换比例不改大小（与 storyboardMode/onLoad 的"保持大小规律"一致）
    const curLong = Math.max(cs.width, cs.height) || 480;
    let newW, newH;
    if (imgRes.w >= imgRes.h) { newW = curLong; newH = curLong * (imgRes.h / imgRes.w); }
    else { newH = curLong; newW = curLong * (imgRes.w / imgRes.h); }
    if (newW < MIN_NODE_WIDTH) { newH = newH * (MIN_NODE_WIDTH / newW); newW = MIN_NODE_WIDTH; }
    console.log(`[ImageNodeView] 重新计算尺寸`, {
      节点ID: id,
      当前尺寸: `${cs.width}×${cs.height}`,
      服务器返回尺寸: `${imgRes.w}×${imgRes.h}`,
      新尺寸: `${Math.round(newW)}×${Math.round(newH)}`
    });
    data.onUpdate?.(id, { customSize: { width: Math.round(newW), height: Math.round(newH) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgRes, data.customSize, data.annotationPreview, data._preserveSize, id]);

  // 选中过 → 画布升级为全图并保持（deselect 后不回退，避免降级闪烁）。
  useEffect(() => { if (isSingleSelect) setShowFullRes(true); }, [isSingleSelect]);

  // ⚡ 按需拉原图：只有当本节点被「单选」（恰好选中它一个）时，才把全图拉下来并回填缓存，
  //   卡片随即由 1024 缩略图无缝升级为全图（decode-then-swap 保证不闪）。
  //   —— 框选/多选（isSingleSelect=false）一律不拉，避免一次选中几十个节点触发下载风暴。
  //   —— 浏览/平移不拉：画布默认只显示缩略图；双击全屏 / 打开 PS / 降级压缩再各自现拉全图。
  //   已有 localCacheUrl（已拉过 / 预热端）则跳过。
  useEffect(() => {
    if (!isSingleSelect || fullImgFetchedRef.current) return;
    if (data.localCacheUrl) return;                 // 已有全图 blob，无需拉
    if (!originalImageUrl) return;                   // 无云 URL（local-cache 未上传等）跳过
    fullImgFetchedRef.current = true;
    let cancelled = false;
    const fileType = data.type === 'video' ? 'video' : data.type === 'audio' ? 'audio' : 'image';
    // crewId/crewName 传 null：L2 会 miss，直接走 L0/L1 命中或 L3 下载并回填
    localCacheService.getLocalUrlOrFetch(originalImageUrl, null, null, fileType)
      .then(res => {
        if (cancelled || !res?.isLocal || !res.url) return;
        handleUpdate({ localCacheUrl: res.url });
      })
      .catch(err => {
        // 🔇 失败静默，不影响用户操作（保持缩略图显示）
        console.debug('拉取全图失败，保持缩略图显示:', err?.message);
      });
    return () => { cancelled = true; };
  }, [isSingleSelect, originalImageUrl, data.localCacheUrl, data.type, handleUpdate]);

  // 只有当 [图片已加载] 且 [时间已到] 时，才算 Finally Loaded
  const isFinallyLoaded = resourceLoaded && minTimePassed;
  const hasContent = !!displayUrl;  // 🆕 使用 displayUrl 判断
  // 注意：data.uploading 不再阻塞显示——上传期间用户可立即编辑本地缓存的图片，
  // 上传状态通过标题 "上传中..." + 右上角云朵图标提示
  const isLoading = isResolvingCache || isRebuildingPreview || (hasContent && !isFinallyLoaded);

  // 真实下载进度：从 localCacheService 的 downloadProgressStore 轮询
  // 下载由 EditorPage → getLocalUrlOrFetch() 触发，进度存储在 localCacheService 模块级 Map 中
  // 此处 500ms 间隔读取，仅触发当前节点重渲染，不影响其他节点
  const [downloadInfo, setDownloadInfo] = useState(null);
  useEffect(() => {
    if (!isLoading || isResolvingCache || !data.output) { setDownloadInfo(null); return; }
    const poll = () => setDownloadInfo(localCacheService.getDownloadProgressByUrl(data.output));
    poll();
    const timer = setInterval(poll, 500);
    return () => clearInterval(timer);
  }, [isLoading, isResolvingCache, data.output]);

  // 修复：生成后图片比例可能变化，强制使用 cover 填满容器，避免黑边
  // 原逻辑是 data.customSize ? 'contain' : 'cover'，但这导致后台生成的新图在老容器里出现黑边
  const fitMode = 'cover';

  const handleEditorSave = (newUrl) => {
    setActiveModal(null); setIsAnnotating(false);
    if (data.onDetach) data.onDetach(id, newUrl, { x: xPos, y: yPos }, 'image');
  };

  // 🆕 确认编辑回调（保留图层，不创建新节点）
  const handleConfirmAnnotation = (previewUrl, annotationData) => {
    setIsAnnotating(false);
    const updates = { annotationPreview: previewUrl, annotationData: annotationData };
    // 裁剪后同步节点尺寸：以裁剪后的画布比例重算 customSize
    if (annotationData?.canvasSize) {
      const { w: cw, h: ch } = annotationData.canvasSize;
      if (cw > 0 && ch > 0) {
        const isMaterialNode = (data.isDirectUpload || data.assetId || data.isDetached) && !data.fromModelNode && !data.mjSourceNodeId;
        const SHORT_SIDE = isMaterialNode ? 120 : 240;
        let newW, newH;
        if (cw >= ch) {
          newH = SHORT_SIDE;
          newW = cw * (SHORT_SIDE / ch);
        } else {
          newW = SHORT_SIDE;
          newH = ch * (SHORT_SIDE / cw);
        }
        updates.customSize = { width: Math.round(newW), height: Math.round(newH) };
      }
    }
    handleUpdate(updates);
  };

  // 🆕 生成初始 annotationData（基于主图和覆盖层）
  const generateInitialAnnotationData = async () => {
    // 🔧 优先使用本地缓存 URL 加载，不依赖云存储
    const loadUrl = data.localCacheUrl || displayUrl || originalImageUrl;
    // 持久化 URL：用于序列化（优先云存储 URL，其次 local-cache://，不存 blob URL）
    const persistImageUrl = originalImageUrl || data.output || null;
    if (!loadUrl) return null;
    
    // 获取主图尺寸
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'Anonymous';
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = loadUrl;
    });
    
    const canvasSize = { w: img.width, h: img.height };
    const layers = [];
    
    // 1️⃣ 底层：主图（imgSrc 使用持久化 URL，渲染时由 annotationRenderer 解析）
    layers.push({
      id: `layer-${Date.now()}-base`,
      type: 'image',
      imgSrc: persistImageUrl,
      visible: true,
      opacity: 1,
      x: 0,
      y: 0,
      w: img.width,
      h: img.height,
      rotation: 0,
      scaleX: 1,
      scaleY: 1
    });
    
    // 2️⃣ 覆盖层：refFiles 中的图片（居中摆放，70% 大小）
    if (data.refFiles?.length > 0) {
      for (const ref of data.refFiles) {
        if (ref.type === 'image' || ref.url?.match(/\.(jpeg|jpg|png|webp|bmp)/i)) {
          try {
            // 🔧 优先使用本地缓存 URL 加载覆盖层图片
            const overlayLoadUrl = ref.localCacheUrl || ref.url;
            const overlayImg = await new Promise((resolve, reject) => {
              const i = new Image();
              i.crossOrigin = 'Anonymous';
              i.onload = () => resolve(i);
              i.onerror = reject;
              i.src = overlayLoadUrl;
            });
            
            const scale = Math.min(canvasSize.w * 0.7 / overlayImg.width, canvasSize.h * 0.7 / overlayImg.height);
            const w = overlayImg.width * scale;
            const h = overlayImg.height * scale;
            const x = (canvasSize.w - w) / 2;
            const y = (canvasSize.h - h) / 2;
            
            layers.push({
              id: `layer-${Date.now()}-overlay-${Math.random()}`,
              type: 'image',
              imgSrc: ref.url,
              visible: true,
              opacity: 1,
              x, y, w, h,
              rotation: 0,
              scaleX: 1,
              scaleY: 1
            });
          } catch (err) {
            console.warn('覆盖层图片加载失败:', ref.url, err);
          }
        }
      }
    }
    
    return { canvasSize, layers };
  };

  // 🆕 合并图层并导出为新节点（不修改原节点，保留多图层编辑能力）
  const handleMergeAndExport = async () => {
    if (isMerging) return;  // 防止重复点击
    setIsMerging(true);
    try {
      // 1️⃣ 获取 annotationData（已有则用已有的，没有则生成初始的）
      let annotationData = data.annotationData;
      if (!annotationData) {
        annotationData = await generateInitialAnnotationData();
        if (!annotationData) {
          console.error('无法生成图层数据');
          return;
        }
        // 保存到节点，以便下次直接用
        handleUpdate({ annotationData });
      }
      
      // 2️⃣ 渲染合并后的图像
      const { renderAnnotationPreview } = await import('../annotationRenderer');
      const mergedUrl = await renderAnnotationPreview(annotationData);
      
      // 3️⃣ 创建新节点（原节点保持不变，保留 refFiles 和 annotationData）
      if (mergedUrl && data.onDetach) {
        data.onDetach(id, mergedUrl, { x: xPos, y: yPos }, 'image');
      }
    } catch (err) {
      console.error('合并图层失败:', err);
    } finally {
      setIsMerging(false);
    }
  };

  // 🆕 降分辨率功能
  const downscaleOptions = imgRes ? getDownscaleOptions(imgRes.w, imgRes.h) : [];
  const canDownscale = downscaleOptions.length > 0;

  const handleDownscale = async (targetOption) => {
    setShowDownscaleMenu(false);
    if (!imgRes || !data.output) return;

    const targetSize = calculateDownscaleSize(imgRes.w, imgRes.h, targetOption.targetPixels);

    // 🔧 保持原格式编码：固定输出 PNG 会把高压缩的 JPEG 照片转成无损大图，
    //    导致"降分辨率后体积反而暴涨"（1.3MB JPEG → 12MB PNG）。
    //    JPEG/WebP 用质量 0.92 编码，PNG 才保持无损。
    let mime = '';
    try { mime = (await (await fetch(displayUrl)).blob()).type; } catch (e) {}
    const m = (mime || '').toLowerCase();
    const isPng = m.includes('png');
    const isWebp = m.includes('webp');
    const outType = isPng ? 'image/png' : (isWebp ? 'image/webp' : 'image/jpeg');
    const quality = isPng ? undefined : 0.92;

    // 使用 Canvas 缩放图片
    const img = new Image();
    img.crossOrigin = 'anonymous'; // 解决跨域问题
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetSize.w;
      canvas.height = targetSize.h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetSize.w, targetSize.h);

      // 🆕 降级就地替换：插入图组首位（触发下游自动更新），原图保留为副图
      canvas.toBlob((blob) => {
        if (!blob) return;
        const blobUrl = URL.createObjectURL(blob);
        if (data.onDownscaleInPlace) {
          data.onDownscaleInPlace(id, blob, blobUrl);
        } else if (data.onDetach) {
          // 兜底：回调未注入时降级为弹出新节点
          data.onDetach(id, blobUrl, { x: xPos, y: yPos }, 'image');
        }
      }, outType, quality);
    };
    img.src = displayUrl;
  };

  // 🆕 读取当前图片的文件体积（命中浏览器/IndexedDB 缓存，几乎零成本）
  // displayUrl 变化（含降分辨率/压缩后就地替换）时重新测量，保持体积框实时
  //   ⚡ 仅视口内测量：displayUrl 常是全尺寸原图，未命中缓存时这次 fetch 就是
  //     整张原图下载。视口外不测，避免打开项目时把全部原图各下一遍。
  useEffect(() => {
    let cancelled = false;
    if (!displayUrl || isResolvingCache) { return; }
    // 🥇 节点已存 fileBytes（生成时写入，自建化优先）→ 直接用，不 fetch/探测
    if (data.fileBytes > 0 && !data.annotationPreview && (originalImageUrl === displayUrl || !data.localCacheUrl)) {
      setFileBytes(data.fileBytes);
      return;
    }
    if (!inViewport) { return; }
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    (async () => {
      try {
        // 自家 OSS 远程原图：先用 image/info 拿体积（几百字节），避免下载全图。
        //   blob:/data:（已缓存全图）直接 fetch 量 size 是免费且准确的，保留。
        if (isOwnOssUrl(displayUrl)) {
          const info = await fetchOssImageInfo(displayUrl, ac?.signal);
          if (cancelled) return;
          if (info?.bytes > 0) { setFileBytes(info.bytes); return; }
        }
        const resp = await fetch(displayUrl, { signal: ac?.signal });
        const blob = await resp.blob();
        if (!cancelled) setFileBytes(blob.size);
      } catch (e) {
        if (!cancelled) setFileBytes(0); // 拿不到体积就不显示框
      }
    })();
    return () => { cancelled = true; ac?.abort(); };
  }, [displayUrl, isResolvingCache, inViewport, data.fileBytes, data.annotationPreview, originalImageUrl, data.localCacheUrl]);

  // 🆕 压缩体积（保持分辨率与原格式）——在 Web Worker 后台执行，不阻塞画布
  const compressTargets = getCompressTargets(fileBytes);
  const handleCompress = async (target) => {
    setShowCompressMenu(false);
    if (!displayUrl || isCompressing) return;
    setIsCompressing(true);
    setCompressProgress(0);
    let worker = null;
    try {
      // 主线程只负责把图 fetch 成 ArrayBuffer（处理 blob:/CORS/local-cache 各种 URL）
      const resp = await fetch(displayUrl);
      const srcBlob = await resp.blob();
      const mime = srcBlob.type || '';
      const imageBuffer = await srcBlob.arrayBuffer();

      worker = new Worker(new URL('../compressWorker.js', import.meta.url));
      compressWorkerRef.current = worker;

      const { buffer, outMime } = await new Promise((resolve, reject) => {
        worker.onmessage = (e) => {
          const msg = e.data;
          if (msg.type === 'progress') {
            const total = Number(msg.total) || 1;
            const current = Number(msg.current) || 0;
            // decode 阶段占 0-15%，encode 阶段占 15-100%
            const pct = msg.phase === 'decode'
              ? Math.round((current / total) * 15)
              : 15 + Math.round((current / total) * 85);
            setCompressProgress(Math.min(99, pct));
          } else if (msg.type === 'done') {
            resolve({ buffer: msg.buffer, outMime: msg.outMime });
          } else if (msg.type === 'error') {
            reject(new Error(msg.message));
          }
        };
        worker.onerror = (err) => reject(new Error(err.message || '压缩 Worker 异常'));
        // Transferable 零拷贝传图进 Worker
        worker.postMessage({ imageBuffer, mime, targetBytes: target.targetBytes }, [imageBuffer]);
      });

      setCompressProgress(100);
      const outBlob = new Blob([buffer], { type: outMime });
      const blobUrl = URL.createObjectURL(outBlob);
      if (data.onDownscaleInPlace) {
        data.onDownscaleInPlace(id, outBlob, blobUrl);
      } else if (data.onDetach) {
        data.onDetach(id, blobUrl, { x: xPos, y: yPos }, 'image');
      }
      setFileBytes(outBlob.size); // 立即反映新体积
    } catch (e) {
      console.error('压缩失败:', e);
    } finally {
      if (worker) { worker.terminate(); }
      compressWorkerRef.current = null;
      setIsCompressing(false);
      setCompressProgress(0);
    }
  };

  // 组件卸载时清理压缩 Worker
  useEffect(() => {
    return () => {
      if (compressWorkerRef.current) {
        compressWorkerRef.current.terminate();
        compressWorkerRef.current = null;
      }
    };
  }, []);

  // 🆕 MJ 反推提示词
  const [isDescribing, setIsDescribing] = useState(false);
  // 🎨 预设工具箱：工具条按「工具集」直显图标（图标=该工具集置顶预设），点击出下拉选预设
  const presetTree = (data.getPresets?.() || { toolsets: [] });
  const presetToolsets = nonEmptyToolsets(presetTree);
  const [openToolsetId, setOpenToolsetId] = useState(null); // 当前展开下拉的工具集 id
  const toolsetMenuRef = useRef(null);
  // 🎯 预设技能箱：图片上方红色横条，直接显示工具集（图标+名字），点击向上弹出预设列表
  const [skillboxData, setSkillboxData] = useState(null);
  const [openSkillsetId, setOpenSkillsetId] = useState(null); // 当前展开的技能集 id
  const skillsetMenuRef = useRef(null);
  useEffect(() => {
    if (openToolsetId == null) return;
    // 点击下拉框以外（含工具条随节点失焦被卸载、ref 变 null 的情况）一律折叠
    const onDocClick = (e) => { if (!toolsetMenuRef.current || !toolsetMenuRef.current.contains(e.target)) setOpenToolsetId(null); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openToolsetId]);
  // 节点失焦（工具条收起）时，同步把下拉框折叠，避免下次选中又自动弹出
  useEffect(() => {
    if (!selected) setOpenToolsetId(null);
  }, [selected]);
  // 🎯 技能箱菜单：点击外部关闭
  useEffect(() => {
    if (openSkillsetId == null) return;
    const onDocClick = (e) => { if (!skillsetMenuRef.current || !skillsetMenuRef.current.contains(e.target)) setOpenSkillsetId(null); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openSkillsetId]);
  // 节点失焦时关闭技能箱菜单
  useEffect(() => {
    if (!selected) setOpenSkillsetId(null);
  }, [selected]);

  // ✨ 技能工具箱数据（从 EditorPage 传来的 ref，三层架构）
  const [realSkillboxData, setRealSkillboxData] = useState(null);
  useEffect(() => {
    if (data.getSkillbox) {
      const sb = data.getSkillbox();
      if (sb && sb.toolsets) {
        // 过滤出有内容的工具集（工具集 → 分组 → 预设）
        const filtered = sb.toolsets
          .map(t => ({
            ...t,
            groups: (t.groups || []).filter(g => g.presets && g.presets.length > 0)
          }))
          .filter(t => t.groups.length > 0);
        setRealSkillboxData({ toolsets: filtered });
      }
    }
  }, [data.getSkillbox, data._skillboxVer]); // 🔧 首次挂载和刷新时都触发（_skillboxVer 可选，无此字段时仍能初始化）

  // 🎯 加载预设工具箱数据（保留原有逻辑）
  useEffect(() => {
    api.get('/api/presets').then(res => {
      if (res && res.toolsets) {
        // 只保留有预设的工具集
        const filtered = res.toolsets.filter(ts => {
          return ts.groups && ts.groups.some(g => g.presets && g.presets.length > 0);
        });
        setSkillboxData({ toolsets: filtered });
      }
    }).catch(err => {
      console.error('Failed to load skillbox:', err);
      setSkillboxData({ toolsets: [] });
    });
  }, []);
  const prevDescribeResultRef = useRef(data.describeResult);
  useEffect(() => {
    if (data.describeResult && data.describeResult !== prevDescribeResultRef.current) {
      prevDescribeResultRef.current = data.describeResult;
      setIsDescribing(false);
      data.onDescribeComplete?.(id, data.describeResult);
    }
  }, [data, data.describeResult, data.onDescribeComplete, id]);
  useEffect(() => {
    if (!isDescribing) return;
    const timer = setTimeout(() => setIsDescribing(false), 120000);
    return () => clearTimeout(timer);
  }, [isDescribing]);

  // 🆕 一键抠图 — 在 Web Worker 中运行，不阻塞主线程
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgRemoveStatus, setBgRemoveStatus] = useState('智能抠图中...');
  const bgWorkerRef = useRef(null);
  const BG_REMOVE_MAX_DIM = 2048;
  const BG_REMOVE_TIMEOUT_MS = 120000;

  // 组件卸载时清理 Worker
  useEffect(() => {
    return () => {
      if (bgWorkerRef.current) {
        bgWorkerRef.current.terminate();
        bgWorkerRef.current = null;
      }
    };
  }, []);

  const handleRemoveBackground = async () => {
    if (!hasContent || isRemovingBg) return;

    setIsRemovingBg(true);
    setBgRemoveStatus('准备图片中...');

    try {
      const bgRemovalPublicPath = `${window.location.origin}/bg-removal/`;
      // 抠图前先尝试用本地文件夹回填浏览器缓存，避免浏览器缓存清空后重复下载。
      await localCacheService.prepareBgRemovalAssets(bgRemovalPublicPath).catch(() => {});

      // 1. 先在主线程把图片 fetch 为 ArrayBuffer（处理 blob: / CORS 等各种 URL）
      const resp = await fetch(displayUrl);
      const sourceBuffer = await resp.arrayBuffer();

      // 对超大图做预缩放，避免在浏览器端推理耗时过长。
      let inputBuffer = sourceBuffer;
      try {
        const blob = new Blob([sourceBuffer], { type: 'image/png' });
        const bitmap = await createImageBitmap(blob);
        const maxSide = Math.max(bitmap.width, bitmap.height);
        if (maxSide > BG_REMOVE_MAX_DIM) {
          setBgRemoveStatus(`图片过大，缩放到 ${BG_REMOVE_MAX_DIM}px 处理中...`);
          const scale = BG_REMOVE_MAX_DIM / maxSide;
          const targetW = Math.max(1, Math.round(bitmap.width * scale));
          const targetH = Math.max(1, Math.round(bitmap.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(bitmap, 0, 0, targetW, targetH);
          const downscaledBlob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => {
              if (b) resolve(b);
              else reject(new Error('缩放失败'));
            }, 'image/png');
          });
          inputBuffer = await downscaledBlob.arrayBuffer();
        }
        bitmap.close?.();
      } catch (e) {
        // 降级路径：如果缩放失败，继续用原图推理。
      }

      // 2. 创建 Worker（每次新建，避免状态残留；用完即终止）
      const worker = new Worker(
        new URL('../bgRemovalWorker.js', import.meta.url)
      );
      bgWorkerRef.current = worker;

      // 3. 用 Promise 包装异步消息
      const resultBuffer = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('抠图超时（资源下载或模型推理过慢）'));
        }, BG_REMOVE_TIMEOUT_MS);

        const finish = (fn, payload) => {
          clearTimeout(timeoutId);
          fn(payload);
        };

        worker.onmessage = (e) => {
          const msg = e.data;
          if (msg.type === 'progress') {
            const total = Number(msg.total) || 0;
            const current = Number(msg.current) || 0;
            if (msg.key === 'fallback:oss') {
              setBgRemoveStatus('切换到国内加速源...');
              return;
            }
            if (msg.key === 'fallback:cdn') {
              setBgRemoveStatus('切换到官方源...');
              return;
            }
            if (msg.key && msg.key.startsWith('fetch:') && total > 0) {
              const percent = Math.min(100, Math.round((current / total) * 100));
              setBgRemoveStatus(`下载模型中 ${percent}%`);
            } else if (msg.key && msg.key.startsWith('fetch:')) {
              setBgRemoveStatus('下载模型中...');
            } else {
              setBgRemoveStatus('模型推理中...');
            }
            return;
          }
          if (msg.type === 'done') {
            finish(resolve, msg.buffer);
          } else if (msg.type === 'error') {
            finish(reject, new Error(msg.message));
          }
        };
        worker.onerror = (err) => finish(reject, new Error(err.message || '抠图 Worker 异常'));

        // 发送图片数据到 Worker（Transferable 零拷贝）
        setBgRemoveStatus('模型初始化中...');
        worker.postMessage(
          {
            imageBuffer: inputBuffer,
            config: {
              model: 'small',
              debug: false,
              publicPath: bgRemovalPublicPath,
              output: { format: 'image/png', quality: 0.8 }
            }
          },
          [inputBuffer]
        );
      });

      // 4. Worker 已返回结果，终止并清理
      worker.terminate();
      bgWorkerRef.current = null;

      // 抠图完成后，把浏览器缓存回填到本地文件夹，确保下次浏览器缓存被清空仍可恢复。
      localCacheService.syncBgRemovalAssetsFromBrowserCache(bgRemovalPublicPath).catch(() => {});

      const blob = new Blob([resultBuffer], { type: 'image/png' });
      const newUrl = URL.createObjectURL(blob);

      if (data.onDetach) {
        data.onDetach(id, newUrl, { x: xPos, y: yPos }, 'image');
      }
    } catch (err) {
      console.error('抠图失败', err);
      if (bgWorkerRef.current) {
        bgWorkerRef.current.terminate();
        bgWorkerRef.current = null;
      }
      alert('一键抠图失败: ' + (err.message || '未知错误') + '。可重试，或先裁剪/降分辨率后再抠图。');
    } finally {
      setBgRemoveStatus('智能抠图中...');
      setIsRemovingBg(false);
    }
  };

  // 🆕 旋转/翻转处理（就地更新节点图片）
  const handleTransformImage = async (transform) => {
    if (!displayUrl) return;
    let corsBlob = null; // 跟踪 CORS 回退时创建的临时 blob URL，用于释放
    // 🛡️ P3: 记录旧的 blob URL，变换成功后释放（防止内存泄漏）
    const oldOutput = data.output;
    const oldLocalCache = data.localCacheUrl;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        // CORS 回退：跨域加载失败时，通过 fetch 获取 blob 再加载
        img.onerror = async () => {
          try {
            const resp = await fetch(displayUrl);
            const blob = await resp.blob();
            corsBlob = URL.createObjectURL(blob);
            img.onload = resolve;
            img.onerror = reject;
            img.src = corsBlob;
          } catch (e) { reject(e); }
        };
        img.src = displayUrl;
      });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const nw = img.naturalWidth, nh = img.naturalHeight;
      if (transform === 'rotate') {
        canvas.width = nh; canvas.height = nw;
        ctx.translate(nh / 2, nw / 2);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, -nw / 2, -nh / 2);
      } else if (transform === 'flipH') {
        canvas.width = nw; canvas.height = nh;
        ctx.translate(nw, 0); ctx.scale(-1, 1);
        ctx.drawImage(img, 0, 0);
      } else if (transform === 'flipV') {
        canvas.width = nw; canvas.height = nh;
        ctx.translate(0, nh); ctx.scale(1, -1);
        ctx.drawImage(img, 0, 0);
      }
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        handleUpdate({ output: blobUrl, localCacheUrl: blobUrl, annotationPreview: null, annotationData: null });
        // 触发上传流程，确保旋转/翻转后的图片持久化到云存储
        const file = new File([blob], `transform-${Date.now()}.png`, { type: 'image/png' });
        if (data.onUploadAndReplace) {
          data.onUploadAndReplace(id, file, blobUrl, data.title);
        }
      }
    } catch (err) {
      console.error('图像变换失败:', err);
    } finally {
      // 释放 CORS 回退时创建的临时 blob URL，避免内存泄漏
      if (corsBlob) URL.revokeObjectURL(corsBlob);
      // 🛡️ P3: 释放旧的 blob URL（仅限 blob: 协议，避免误释放 OSS/本地缓存 URL）
      if (oldOutput && oldOutput.startsWith('blob:') && oldOutput !== oldLocalCache) {
        URL.revokeObjectURL(oldOutput);
      }
      if (oldLocalCache && oldLocalCache.startsWith('blob:') && oldLocalCache !== oldOutput) {
        URL.revokeObjectURL(oldLocalCache);
      }
    }
  };

  const capsuleStyle = {
    display: 'flex', alignItems: 'center', gap: '1px',
    background: 'var(--toolbar-bg)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '50px', padding: '4px 8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)'
  };

  const dividerStyle = { width: '1px', height: '12px', background: 'var(--border-strong)', margin: '0 2px' };

  // 🆕 获取上一层级的图片作为底图（Overlay）— 使用 useRef 浅比较避免不必要的重新计算
  // 返回完整的 ref 对象数组，而不仅仅是 URL，以便在渲染时使用稳定的 key
  const prevRefKeyRef = useRef('');
  const prevOverlayRefs = useRef([]);
  const overlayRefs = useMemo(() => {
    const imageRefs = (data.refFiles || []).filter(ref => 
      ref.type === 'image' || (typeof ref.url === 'string' && ref.url.match(/\.(jpeg|jpg|png|webp|bmp)/i))
    );
    // 用轻量 key 比较代替 JSON.stringify：只拼接 id+url
    const key = imageRefs.map(f => `${f.id}|${f.url}`).join(',');
    if (key === prevRefKeyRef.current) return prevOverlayRefs.current;
    prevRefKeyRef.current = key;
    prevOverlayRefs.current = imageRefs;
    return imageRefs;
  }, [data.refFiles]);

  // 全量上游图片 URL 数组 — 仅用于 annotationData 中 blob URL 修复（不传给编辑器）
  const overlayUrls = useMemo(() => overlayRefs.map(ref => ref.url), [overlayRefs]);

  // 🆕 过滤掉已被 AI 生成消费的参考图 — 这些 ref 只是给 AI API 用的，不应作为图层叠加
  // 用节点 ID 匹配（稳定），不用 URL（可能在生成过程中从 blob 变成云存储 URL）
  const activeOverlayRefs = useMemo(() => {
    if (!data.generationRefIds || data.generationRefIds.length === 0) return overlayRefs;
    const consumedSet = new Set(data.generationRefIds);
    return overlayRefs.filter(ref => !consumedSet.has(ref.id));
  }, [overlayRefs, data.generationRefIds]);

  /**
   * 🔍 unbakedOverlayRefs — 节点画布上实时显示的"未烘焙"叠加图片
   *
   * 定义：activeOverlayRefs 中，尚未被编辑器保存为图层（即未出现在 annotationData.layers）
   *   的上游图片。这些图片以半透明浮层形式叠加在节点预览上，提示用户"此图还未进入图层"。
   *
   * 匹配逻辑 v2（基于 sourceUrl，解决抠图后文件名失配问题）：
   *   已烘焙标识集合（bakedIdentifiers）同时包含：
   *     1. 完整 sourceUrl（原始云存储 URL，永不变）
   *     2. getFilename(sourceUrl)（文件名，兜底匹配）
   *     3. getFilename(imgSrc）（兜底：未经抠图的普通图层）
   *
   *   旧方案 v1：只用 getFilename(imgSrc)，抠图后 imgSrc 变为 data URL，
   *   getFilename 返回乱码 → 误判为"未烘焙" → 节点上出现多余浮层。
   *
   * 黑名单排除：
   *   deletedOverlayUrls 中的图片（用户主动删除）也不应显示为浮层。
   *   使用文件名匹配，兼容 CDN 域名/签名变化。
   */
  const unbakedOverlayRefs = useMemo(() => {
    if (!data.annotationData || activeOverlayRefs.length === 0) return activeOverlayRefs;
    // 🔧 使用 sourceUrl（原始上游 URL）进行匹配，编辑后 imgSrc 可能变为 data URL 导致文件名不匹配
    const bakedIdentifiers = new Set(
      data.annotationData.layers
        .filter(l => l.type === 'image' && l.id !== 'bg')
        .flatMap(l => [
          l.sourceUrl,
          getFilename(l.sourceUrl),
          getFilename(l.imgSrc)
        ].filter(Boolean))
    );
    // 被用户显式删除的 overlay 也不应显示
    const deletedFilenames = new Set(
      (data.annotationData.deletedOverlayUrls || []).map(u => getFilename(u)).filter(Boolean)
    );
    return activeOverlayRefs.filter(ref => {
      const fn = getFilename(ref.url);
      if (bakedIdentifiers.has(ref.url) || bakedIdentifiers.has(fn)) return false;
      if (deletedFilenames.has(fn)) return false;
      return true;
    });
  }, [activeOverlayRefs, data.annotationData]);

  // 清理 annotationData 中的失效 blob URL（兼容旧数据）
  useEffect(() => {
    if (data.annotationData && data.annotationData.layers && !blobUrlFixedRef.current) {
      const hasBlobUrl = data.annotationData.layers.some(
        l => l.type === 'image' && l.imgSrc && l.imgSrc.startsWith('blob:')
      );
      if (hasBlobUrl && overlayUrls.length > 0) {
        const cleaned = {
          ...data.annotationData,
          layers: data.annotationData.layers.map((layer, index) => {
            if (layer.type === 'image' && layer.imgSrc && layer.imgSrc.startsWith('blob:')) {
              let realUrl = layer.imgSrc;
              if (layer.id === 'bg') {
                realUrl = originalImageUrl;
              } else if (layer.id.startsWith('overlay-')) {
                const overlayIndex = data.annotationData.layers
                  .filter((l, i) => i < index && l.id.startsWith('overlay-'))
                  .length;
                realUrl = overlayUrls[overlayIndex] || layer.imgSrc;
              }
              return { ...layer, imgSrc: realUrl, sourceUrl: layer.sourceUrl || realUrl };
            }
            return layer;
          })
        };
        const hasChanges = cleaned.layers.some((layer, i) => 
          layer.imgSrc !== data.annotationData.layers[i]?.imgSrc
        );
        if (hasChanges) {
          blobUrlFixedRef.current = true;  // 标记已修复，避免重复触发
          handleUpdate({ annotationData: cleaned });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.annotationData, overlayUrls, originalImageUrl]);

  // 🆕 多图层模式检测（带延迟稳定，避免初始加载闪烁）
  // 条件：有 annotationData（用过编辑器）或有未被消费的活跃叠加图
  // 仅有被 AI 消费过的参考图不算多图层模式
  // ⚠️ 生成节点（AI模型节点）永远不进入多图层模式 — 上游连图只进控制台 refFiles，不触发图层编辑
  const isGenerateNode = !data.isDirectUpload && !data.assetId && !data.isDetached;
  useEffect(() => {
    const shouldBeMultiLayer = !isGenerateNode && !!(data.annotationData || activeOverlayRefs.length > 0);
    
    // 如果状态需要改变，添加小延迟避免闪烁
    if (shouldBeMultiLayer !== isMultiLayerMode) {
      const timer = setTimeout(() => {
        setIsMultiLayerMode(shouldBeMultiLayer);
      }, 50);  // 50ms 延迟，避免初始加载时的快速切换
      return () => clearTimeout(timer);
    }
  }, [data.annotationData, activeOverlayRefs, isMultiLayerMode, isGenerateNode]);

  return (
    <>
      <Suspense fallback={
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.35)',
          pointerEvents: 'none',
        }}>
          <Loader2 size={18} strokeWidth={2} color="rgba(255,255,255,0.6)" style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginTop: 3, fontSize: 5, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.3px' }}>加载编辑器...</span>
        </div>
      }>
        {isAnnotating && (
          <AnnotationModal 
            imageUrl={originalImageUrl || data.localCacheUrl}
            localImageUrl={data.localCacheUrl}
            persistUrl={originalImageUrl || data.output}
            overlayUrls={activeOverlayRefs.map(ref => ref.url)}
            localOverlayUrls={activeOverlayRefs.map(ref => ref.localCacheUrl).filter(Boolean)}
            onClose={() => setIsAnnotating(false)} 
            onSave={handleEditorSave}
            initialAnnotationData={data.annotationData}
            onConfirmWithLayers={handleConfirmAnnotation}
          />
        )}
        
        {activeModal === 'crop' && (
          <ImageCropperModal imageUrl={displayUrl} fallbackUrl={originalImageUrl || data.output} mode={activeModal} onClose={() => setActiveModal(null)} onSave={handleEditorSave} />
        )}
        
        {isFullscreen && (
          <FullscreenModal url={displayUrl} fallbackUrl={originalImageUrl} type="image" generationTime={data.generationTime} onClose={() => setIsFullscreen(false)} />
        )}
      </Suspense>

      {/* Describe 反推遮罩 */}
      {isDescribing && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 1002,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          borderRadius: 'inherit',
          pointerEvents: 'all',
        }}>
          <div style={{ width: 36, height: 36, border: '1.5px solid rgba(255,255,255,0.15)', borderTop: '1.5px solid rgba(255,255,255,0.7)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ marginTop: 8, fontSize: 9, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.4px', fontWeight: 400 }}>Prompt 推理中...</span>
        </div>
      )}

      {/* 【新增】错误遮罩 */}
      {/* 错误遮罩 + 操作按钮 */}
      {data.error && (
        <div style={{
          position: 'absolute',
          top: '0', left: '0', right: '0', bottom: '0',
          background: 'rgba(0,0,0,0.55)',
          zIndex: 1002,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 500, marginBottom: 18, textShadow: '0 2px 8px #000' }}>{data.error}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <ToolBtn
              onClick={() => data.onGenerate?.(id)}
              title="重新生成"
              color="#94a3b8"
              size="32px"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s',
                width: '32px', height: '32px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              }}
            >
              <RefreshCw size={18} color="#94a3b8" strokeWidth={2} />
            </ToolBtn>
            <ToolBtn
              onClick={() => { handleUpdate({ error: null }); if (showConsole) onToggleConsole(); }}
              title="关闭"
              color="#94a3b8"
              size="32px"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s',
                width: '32px', height: '32px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              }}
            >
              <Minimize2 size={18} color="#94a3b8" strokeWidth={2} />
            </ToolBtn>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════
          Shimmer 占位层（加载阶段）
          ════════════════════════════════════════════════════════════
          数据来源：node.data.thumbUrl，由服务端 routes/ai.js 在 AI 生成完成后
          调 utils/generateThumb.js 用 sharp 压成 400px webp 上传 OSS 写回。
          视觉过渡：低清缩略图 (blur 2px) → 原图 fade-in 220ms 覆盖。
          老节点（无 thumbUrl）退化为纯深灰 #1a1a1a shimmer，无副作用。 */}
      {isLoading && (
        <div className="shimmer-wrapper" style={{
            ...styles.mediaFull, borderRadius: '0px',
            position: 'absolute', top: 0, left: 0, zIndex: 1
        }}>
          {data.thumbUrl && (
            <img
              src={data.thumbUrl}
              alt=""
              aria-hidden
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'cover',
                filter: 'blur(2px)',  // 轻度模糊：掩盖压缩瑕疵，与原图过渡更自然
                pointerEvents: 'none',
                zIndex: 0,
              }}
            />
          )}
          <div style={{ position:'absolute', inset:0, zIndex:2, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
            <Loader2 size={16} strokeWidth={2} color="rgba(255,255,255,0.5)" style={{ animation:'spin 1s linear infinite' }} />
            <span style={{ marginTop:4, fontSize:9, color:'rgba(255,255,255,0.45)', letterSpacing:'0.3px', fontWeight:500 }}>
              {isResolvingCache ? '正在读取缓存...'
                : downloadInfo && downloadInfo.status === 'downloading'
                  ? downloadInfo.total > 0
                    ? `正在加载图片 ${Math.round((downloadInfo.downloaded / downloadInfo.total) * 100)}% (${formatBytes(downloadInfo.downloaded)}/${formatBytes(downloadInfo.total)})`
                    : `正在加载图片... ${formatBytes(downloadInfo.downloaded)}`
                  : '正在加载图片...'}
            </span>
          </div>
        </div>
      )}

      {/* ☁️ 上传中指示器：图片可编辑，右下角小图标提示后台上传 */}
      {data.uploading && !isLoading && (
        <div style={{
            position: 'absolute', bottom: 6, right: 6, zIndex: 25,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)', borderRadius: '50%',
            width: 24, height: 24,
            color: '#93c5fd', pointerEvents: 'none',
            backdropFilter: 'blur(4px)',
        }}>
            <CloudUpload size={13} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
      )}

      {hasContent ? (
        <>
          {/* 🎨 技能工具箱 — 紫色胶囊横条，图片节点上方（仿照 MJ / GPT 局部重绘样式） */}
          {(() => {
            const shouldShow = selected && isSingleSelect && realSkillboxData && realSkillboxData.toolsets && realSkillboxData.toolsets.length > 0 && data.onApplySkillbox;
            return shouldShow ? (
              <SkillboxToolbar
                nodeId={id}
                toolsets={realSkillboxData.toolsets}
                onApply={(nodeId, preset, selectedRefImages) => data.onApplySkillbox(nodeId, preset.id, selectedRefImages)}  // 传递参考图数组
                zoom={zoom}
              />
            ) : null;
          })()}

          {/* 🎯 预设工具箱 — 红色横条（保留原有功能） */}
          {/* 暂时注释掉，skillboxData 现在用于旧预设系统 */}

          <img
            ref={observeCardEl}
            src={committedSrc || displayUrl}
            decoding="async"
            style={{
                ...styles.mediaFull, borderRadius: '0px',
                // 🎨 fade-in：图片像素未到屏幕前 opacity=0，避免棋盘格底色"白闪"
                // shimmer-wrapper 的深灰背景会一直撑到 onLoad 后这里 opacity 变 1
                opacity: isFinallyLoaded ? 1 : 0,
                transition: 'opacity 220ms ease-out',
                objectFit: fitMode, // Use calculated fit mode
                // 🎨 棋盘格调暗：原 #c8c8c8 / #e8e8e8 在黑画布上对比强烈，是闪白主因
                // 改为深色棋盘（#3a3a3a / #2a2a2a），仍能体现透明 PNG 的 alpha 区域
                backgroundImage: 'linear-gradient(45deg, #3a3a3a 25%, transparent 25%), linear-gradient(-45deg, #3a3a3a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #3a3a3a 75%), linear-gradient(-45deg, transparent 75%, #3a3a3a 75%)',
                backgroundSize: '10px 10px',
                backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                backgroundColor: '#2a2a2a', // 棋盘格底色（透明区域显示），与节点底色融合
                cursor: 'pointer' // 【新增】变成手型，提示可点击
            }}
            alt=""
            // 🛡️ 加载失败：先尝试回退云 URL，再延迟重试（最多 3 次）
            onError={(e) => {
                // 第一优先：blob URL 失效 → 回退到云存储 URL
                if (data.output && e.target.src !== data.output && !data.output.startsWith('local-cache://')) {
                    e.target.src = data.output;
                    return;
                }
                // 第二优先：云 URL 也失败 → 延迟重试（CDN 冷启动/网络抖动通常 2 秒恢复）
                const MAX_RETRIES = 3;
                setImgRetryCount(prev => {
                    const next = prev + 1;
                    if (next <= MAX_RETRIES) {
                        const delay = next * 2000; // 2s, 4s, 6s
                        imgRetryTimerRef.current = setTimeout(() => {
                            if (e.target) {
                                const base = data.output || displayUrl;
                                if (base && !base.startsWith('blob:') && !base.startsWith('data:')) {
                                    const sep = base.includes('?') ? '&' : '?';
                                    e.target.src = `${base}${sep}_r=${Date.now()}`;
                                }
                            }
                        }, delay);
                    } else if (!resolveUrlTriedRef.current) {
                        // 🛡️ A3 兜底：重试用尽仍失败 → 可能是 crew 改名后的过期路径。
                        //   问服务端按当前 crew 名修正 URL（HEAD 校验命中才返回），
                        //   拿到就替换并落库。只尝试一次，避免循环。
                        resolveUrlTriedRef.current = true;
                        const bad = data.output;
                        const pid = (window.location.pathname.match(/\/editor\/(\d+)/) || [])[1];
                        if (pid && bad && /^https?:\/\//.test(bad)) {
                            api.get(`/api/projects/${pid}/resolve-url?url=${encodeURIComponent(bad)}`)
                              .then(r => {
                                if (r?.fixed && r.url && r.url !== bad) {
                                    handleUpdate({ output: r.url, localCacheUrl: null });
                                }
                              })
                              .catch(() => {});
                        }
                    }
                    return next;
                });
            }}
            // 【新增】双击全屏
            onDoubleClick={(e) => {
                e.stopPropagation();
                setIsFullscreen(true);
            }}
            onLoad={(e) => {
                // 分辨率标签需要「原图绝对像素」，但可见 <img> 可能是 768 缩略图，
                // naturalWidth 会偏小。故此处仅作为「临时值」填入（比例正确），
                // 真值由下方视口内全尺寸探测（fullResProbe effect）覆盖。
                // 已由全尺寸探测确定过的，不被缩略图 onLoad 覆盖。
                const nw0 = e.target.naturalWidth, nh0 = e.target.naturalHeight;
                // 当前贴的若是全图（blob 缓存/原图，非缩略图）→ naturalWidth 就是真值，
                //   直接落缓存并锁定，覆盖「全屏看过回画布标签才有」的场景。
                const showingFull = committedSrc && (
                  committedSrc.startsWith('blob:') || committedSrc === originalImageUrl
                  || (!isOwnOssUrl(originalImageUrl || '') && committedSrc === displayUrl)
                );
                if (!fullResMeasuredRef.current) {
                  if (nw0 && nh0) setImgRes({ w: nw0, h: nh0 });
                  if (showingFull && originalImageUrl && nw0 && nh0) {
                    fullResMeasuredRef.current = true;
                    setCachedDimensions(originalImageUrl, nw0, nh0);
                  }
                }
                setResourceLoaded(true);

                // 🔧 关键修复：如果当前显示的是 annotationPreview（PS 编辑后的预览），不重新计算尺寸
                // annotationPreview 只是可视化图层合成结果，不应改变节点的原始尺寸
                if (data.annotationPreview && displayUrl === data.annotationPreview) {
                    return; // 跳过尺寸计算
                }

                // 【修正】：自动调整尺寸逻辑
                const natW = e.target.naturalWidth;
                const natH = e.target.naturalHeight;

                // 🔧 当前贴的是「可能被裁剪的缩略图」（自家 OSS 且未升级全图）→ 它的 naturalWidth
                //   是裁剪后比例（素材库缩略图常裁成 1:1/角色竖图），据此算框会把节点拉竖。
                //   此时不按缩略图算，交给上方「imgRes 原图真值」effect 用原图比例校正。
                //   （非自家 OSS 图 cardSrc 直接返回原图，natW 本就是真值，不受影响）
                const paintedIsCroppableThumb = isOwnOssUrl(originalImageUrl || '') && !showingFull;
                if (paintedIsCroppableThumb) {
                    return;
                }

                const isNewImage = data.output !== prevOutputRef.current;
                if (isNewImage) {
                    prevOutputRef.current = data.output;
                }
                
                // 🔧 比例不匹配检测：覆盖"离开房间后回来"的场景
                // 此时 DB 有旧 customSize + 新 output，prevOutputRef 初始化为当前 output（看不出是新图），
                // 但 customSize 的比例和实际图片比例不一致 → 强制重算
                let ratioMismatch = false;
                if (data.customSize && data.customSize.width > 0 && data.customSize.height > 0 && natW && natH) {
                    const nodeRatio = data.customSize.width / data.customSize.height;
                    const mediaRatio = natW / natH;
                    // 容差 5%：避免浮点精度误触发
                    ratioMismatch = Math.abs(nodeRatio - mediaRatio) / mediaRatio > 0.05;
                }
                
                // 如果是新图、没有 customSize、或比例不匹配，都要重新计算尺寸
                // 🔧 降级就地替换 / 切换首图：_preserveSize 期间跳过重算，保持原节点卡片尺寸
                if (data._preserveSize) {
                  data.onUpdate?.(id, { _preserveSize: undefined });
                  return;
                }
                const shouldRecalculate = isNewImage || !data.customSize || data.customSize.width <= 0 || data.customSize.height <= 0 || ratioMismatch;
                
                if (!shouldRecalculate) {
                    // 已有尺寸、不是新图、比例匹配 → 跳过自动计算
                    return;
                }
                
                if (natW && natH && data.onUpdate) {
                    let newW, newH;

                    if (data.storyboardMode) {
                        // 🎬 分镜大师节点：回填新图后按图片比例自动匹配框，但保持节点原有"大小规律"——
                        //   以当前节点长边为基准（默认 480），只换比例不缩小。
                        //   避免 1:1 → 9:16 后竖图被塞进旧方框、或缩成普通生成节点的小尺寸。
                        const curLong = Math.max(data.customSize?.width || 0, data.customSize?.height || 0) || 480;
                        if (natW >= natH) {
                            // 横图或正方形：以长边作宽
                            newW = curLong;
                            newH = curLong * (natH / natW);
                        } else {
                            // 竖图：以长边作高
                            newH = curLong;
                            newW = curLong * (natW / natH);
                        }
                    } else {
                    // 判断是素材节点还是生成节点（从模型节点剥离的不算素材节点）
                    // MJ Action 节点（有 mjSourceNodeId）也不算素材节点，保持与生成节点同尺寸
                    const isMaterialNode = (data.isDirectUpload || data.assetId || data.isDetached) && !data.fromModelNode && !data.mjSourceNodeId;
                    const SHORT_SIDE = isMaterialNode ? 120 : 240;

                    if (natW >= natH) {
                        // 横图或正方形：高度固定
                        newH = SHORT_SIDE;
                        newW = natW * (SHORT_SIDE / natH);
                    } else {
                        // 竖图：宽度固定
                        newW = SHORT_SIDE;
                        newH = natH * (SHORT_SIDE / natW);
                    }
                    }

                    // 🔧 横向宽度下限：竖图宽度过小会让顶部信息条（分辨率+文件大小）溢出、
                    //    盖住关闭按钮。强制保证最小宽度并按比例重算高度，与 calculateLongSide 一致。
                    if (newW < MIN_NODE_WIDTH) {
                        newH = newH * (MIN_NODE_WIDTH / newW);
                        newW = MIN_NODE_WIDTH;
                    }

                    // 延迟执行避免 React 渲染冲突
                    setTimeout(() => {
                         data.onUpdate(id, { customSize: { width: Math.round(newW), height: Math.round(newH) } });
                    }, 50);
                }
            }} 
          />
          {/* 🆕 连线叠加预览：上游图片居中显示（生成节点不显示叠加浮层，上游图只进控制台） */}
          {!isGenerateNode && unbakedOverlayRefs.length > 0 && unbakedOverlayRefs.map((ref) => (
            <img
              key={ref.url}
              src={ref.localCacheUrl || ossThumb(ref.url, 768)}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '70%', maxHeight: '70%',
                objectFit: 'contain',
                opacity: 0.85,
                pointerEvents: 'none',
                zIndex: 2,
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))'
              }}
            />
          ))}
          {/* � 多图层模式：蓝色内框指示（边框宽度 6px，不透明度 60%）*/}
          {isMultiLayerMode && (
            <div style={{
              position: 'absolute',
              inset: 0,
              border: '6px solid rgba(59, 130, 246, 0.60)',
              borderRadius: 'inherit',
              pointerEvents: 'none',
              zIndex: 3,
            }} />
          )}
          <NodeResolutionTag 
            w={imgRes?.w} 
            h={imgRes?.h} 
            generationTime={data.generationTime}
            canDownscale={canDownscale}
            downscaleOptions={downscaleOptions}
            onDownscale={handleDownscale}
            showMenu={showDownscaleMenu}
            onToggleMenu={() => { setShowDownscaleMenu(!showDownscaleMenu); setShowCompressMenu(false); }}
            fileBytes={fileBytes}
            compressTargets={compressTargets}
            onCompress={handleCompress}
            showCompressMenu={showCompressMenu}
            onToggleCompressMenu={() => { setShowCompressMenu(!showCompressMenu); setShowDownscaleMenu(false); }}
            compressing={isCompressing}
            uploading={!!data.uploading || isResolvingCache}
          />
          {/* PSD 标识 / 合并按钮 — 只在真正的多图层模式下显示（生成节点排除） */}
          {!isGenerateNode && (data.annotationData || activeOverlayRefs.length > 0) && (
            <div style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1001 }}>
            <QuickTooltip content={isMerging ? '合并导出中...' : '多图层模式：点击合并导出'} position="top" delay={200}>
            <div 
              onClick={(e) => { e.stopPropagation(); if (!isMerging) handleMergeAndExport(); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24,
                borderRadius: '50%',
                background: isMerging ? 'rgba(59, 130, 246, 1)' : 'rgba(59, 130, 246, 0.60)',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                cursor: isMerging ? 'wait' : 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { 
                if (!isMerging) {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 1)';
                }
              }}
              onMouseLeave={(e) => { 
                if (!isMerging) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.60)';
                }
              }}
            >
              {isMerging ? (
                <Loader2 size={14} strokeWidth={2} color="white" style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              )}
            </div>
            </QuickTooltip>            </div>          )}
          {/* 🆕 抠图中 overlay */}
          {isRemovingBg && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1002,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 'inherit',
              pointerEvents: 'none',
            }}>
              <Loader2 size={18} strokeWidth={2} color="rgba(255,255,255,0.6)" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginTop: 3, fontSize: 5, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.3px' }}>{bgRemoveStatus}</span>
            </div>
          )}
          {/* 🆕 压缩中 overlay（后台 Worker 处理，带进度环） */}
          {isCompressing && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1002,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.45)',
              borderRadius: 'inherit',
              pointerEvents: 'none',
            }}>
              <Loader2 size={18} strokeWidth={2} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginTop: 4, fontSize: 9, fontWeight: 700, color: '#93c5fd', letterSpacing: '0.3px' }}>
                压缩中 {compressProgress}%
              </span>
            </div>
          )}
        </>
      ) : (
        <ImageIcon size={48} color="rgba(255,255,255,0.4)" strokeWidth={2} />
      )}

      {selected && (
        <div className="nodrag" style={{ 
            position: 'absolute', top: '100%', left: '0', right: '0', height: '0', zIndex: 1001 
        }}>
          <div style={{
            position: 'absolute', top: '2px', left: '50%',
            transform: `translateX(-50%) scale(${(1 / zoom) * 0.9})`, 
            transformOrigin: 'top center',
            ...capsuleStyle
          }}>
            {/* 🔒 多图层(PSD)模式下禁用：抠图、下载、更换素材 */}
            {(() => { const hasPsd = !!data.annotationData; return (<>
              {!isGenerateNode && <>
              <ToolBtn disabled={!hasContent} onClick={() => { data.onDisconnectDownstream?.(id); setIsAnnotating(true); }} title="图像编辑器">
                <img 
                  src={`${API_BASE_URL}/logos/ps.svg`}
                  alt="PS"
                  width="22"
                  height="22"
                  style={{ display: 'block', opacity: hasContent ? 1 : 0.3 }}
                />
              </ToolBtn>
              <div style={dividerStyle}></div>
              </>}

            {/* 第二组：工具集入口（预设工具箱）— 将来新增的工具集都归入这组 */}
            {/* 🎨 预设工具箱 — 每个工具集一个图标（=置顶预设），点击出下拉，内部按分组显示 图标+预设名 */}
            {presetToolsets.length > 0 && !!data.onApplyPreset && presetToolsets.map(ts => {
              const top = getTopPreset(ts);
              if (!top) return null;
              const isOpen = openToolsetId === ts.id;
              return (
                <div key={ts.id} ref={isOpen ? toolsetMenuRef : null} style={{ position: 'relative', display: 'flex' }}>
                  <ToolBtn
                    disabled={!hasContent}
                    active={isOpen}
                    onClick={() => {
                      console.log('[ImageNodeView] 工具箱按钮点击', { hasContent, tsId: ts.id, openToolsetId });
                      if (hasContent) setOpenToolsetId(v => v === ts.id ? null : ts.id);
                    }}
                    title={!hasContent ? '无图像' : top.name}
                  >
                    {top.logoUrl ? (
                      <img src={top.logoUrl} alt={top.name} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2 }}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                    ) : null}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: top.logoUrl ? 'none' : 'block' }}>
                      <path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M15 9h.01M17.8 6.2L19 5M3 21l9-9M12.2 6.2L11 5"/>
                    </svg>
                  </ToolBtn>
                  {isOpen && (
                    <div className="custom-scrollbar nowheel" style={{
                      position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                      marginTop: 6, minWidth: 190, maxWidth: 260, maxHeight: 340, overflowY: 'auto',
                      background: 'var(--toolbar-bg)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.5)', padding: 6, zIndex: 1100, cursor: 'default'
                    }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ fontSize: 10, color: '#daa520', fontWeight: 600, padding: '2px 8px 6px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 4 }}>{ts.name}</div>
                      {(ts.groups || []).map(g => (
                        <div key={g.id} style={{ marginBottom: 4 }}>
                          <div style={{ fontSize: 9, color: '#64748b', padding: '4px 8px 2px', fontWeight: 600, letterSpacing: '0.5px' }}>{g.name}</div>
                          {(g.presets || []).map(p => (
                            <div key={p.id}
                              onClick={() => { setOpenToolsetId(null); data.onApplyPreset(id, p.id); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', transition: 'background 0.12s' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              {p.logoUrl ? (
                                <img src={p.logoUrl} alt={p.name} style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                  <path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M15 9h.01M17.8 6.2L19 5M3 21l9-9M12.2 6.2L11 5"/>
                                </svg>
                              )}
                              <span style={{ fontSize: 12, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div style={dividerStyle}></div>

            {/* 第三组：一键抠图 + MJ 反推提示词 */}
            <ToolBtn disabled={!hasContent || hasPsd} onClick={() => { if(!isRemovingBg) handleRemoveBackground(); }} title={hasPsd ? "多图层模式下不可用" : !hasContent ? "无图像" : "一键抠图(Beta)"}>
              {isRemovingBg ? (
                <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 1024 1024" fill="currentColor">
                  <path d="M727.936 690.688l244.992 273.536a29.568 29.568 0 0 1-4.224 43.328 35.008 35.008 0 0 1-47.168-4.608l-244.992-273.536a29.568 29.568 0 0 1 2.304-41.728l1.92-1.6a35.008 35.008 0 0 1 47.168 4.608z m73.472-609.6a32 32 0 0 1 3.2 20.352l-46.528 271.36 197.184 192.256a32 32 0 0 1-17.728 54.592l-272.512 39.552-121.856 246.976a32 32 0 0 1-57.408 0L363.904 659.2l-272.512-39.552a32 32 0 0 1-17.728-54.592l197.184-192.256-46.528-271.36a32 32 0 0 1 46.4-33.728L514.56 195.84l243.712-128.128a32 32 0 0 1 43.2 13.44z m-54.592 62.72l-226.304 113.92-227.52-113.92 43.968 241.92-197.568 192 268.928 29.696 112.192 226.368L626.304 607.36l254.72-29.632-186.048-192 51.84-241.984z"/>
                </svg>
              )}
            </ToolBtn>

            {/* MJ 反推提示词按钮 — 仅当工具箱已配置时显示 */}
            {data.getMjDescribeConfig?.()?.enabled && !!data.onDescribe && (
              <ToolBtn
                disabled={!hasContent || isDescribing}
                onClick={() => { if (!isDescribing) { setIsDescribing(true); data.onDescribe(id); } }}
                title={!hasContent ? '无图像' : isDescribing ? '反推中...' : 'MJ 反推提示词'}
              >
                {isDescribing ? (
                  <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                )}
              </ToolBtn>
            )}

            <div style={dividerStyle}></div>

            {/* 第四组：快照/裁剪 + 旋转 + 水平翻转 + 垂直翻转 */}
            <ToolBtn disabled={!hasContent} onClick={() => { setActiveModal('crop'); }} title="快照/裁剪">
              <Camera size={18} strokeWidth={1.5} />
            </ToolBtn>
            <ToolBtn disabled={!hasContent || hasPsd} onClick={() => { handleTransformImage('rotate'); }} title={hasPsd ? "多图层模式下不可用" : !hasContent ? "无图像" : "旋转90°"}>
              <RotateCw size={16} strokeWidth={1.5} />
            </ToolBtn>
            <ToolBtn disabled={!hasContent || hasPsd} onClick={() => { handleTransformImage('flipH'); }} title={hasPsd ? "多图层模式下不可用" : !hasContent ? "无图像" : "水平翻转"}>
              <FlipHorizontal size={16} strokeWidth={1.5} />
            </ToolBtn>
            <ToolBtn disabled={!hasContent || hasPsd} onClick={() => { handleTransformImage('flipV'); }} title={hasPsd ? "多图层模式下不可用" : !hasContent ? "无图像" : "垂直翻转"}>
              <FlipVertical size={16} strokeWidth={1.5} />
            </ToolBtn>
            
            <div style={dividerStyle}></div>
            
            <ToolBtn
              disabled={!hasContent || (hasPsd && !isGenerateNode)}
              onClick={() => { downloadFile(data.localCacheUrl || data.output, buildDownloadFilename(data.title, data.output, '.png')); }}
              title={hasPsd && !isGenerateNode ? "多图层模式下不可用" : !hasContent ? "无图像" : "下载"}
            >
              <Download size={18} strokeWidth={1.5} />
            </ToolBtn>

            <ToolBtn disabled={hasPsd} onClick={() => { data.onReplace?.(id, 'image'); }} title={hasPsd ? "多图层模式下不可用" : "更换素材"}>
              <RefreshCw size={16} strokeWidth={1.5} />
            </ToolBtn>
            
            <ToolBtn disabled={!hasContent} onClick={() => { setIsFullscreen(true); }} title={!hasContent ? "无图像" : "全屏"}>
              <Maximize size={18} strokeWidth={1.5} />
            </ToolBtn>

            {/* 🎛️ 控制台开关按钮 - 只在生成节点（非容器节点）显示 */}
            {!data.isDirectUpload && !data.assetId && (
              <QuickTooltip content={showConsole ? "收起控制台" : "打开控制台"} position="top">
                <div 
                  onClick={onToggleConsole}
                  style={{
                    width: '28px', height: '28px',
                    borderRadius: '50%',
                    background: 'var(--console-toggle-bg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(255,255,255,0.2)',
                    marginLeft: '4px'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--console-toggle-hover)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--console-toggle-bg)';
                  }}
                >
                  <ChevronDown size={16} style={{
                    transform: showConsole ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    color: 'var(--console-toggle-fg)'
                  }} />
                </div>
              </QuickTooltip>
            )}
            </>); })()}
          </div>
        </div>
      )}
    </>
  );
});